import React, { useEffect, useState } from "react";
import {
  getMembersVariables,
  PersonalizationValue,
  TriggerStepCreate,
} from "@dots-projects/types";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { Controller, useForm } from "react-hook-form";
import useGuildTags from "../Settings/useGuildTags";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import produce from "immer";
import { FormLabel } from "./FormLabel";
import { Button, Divider, Input, Select, SelectProps, Spin } from "antd";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import DiscordMemberSingleSelector from "./DiscordSingleMemberSelector";

/**
 * Renders the form for a "Edit Dots tags" step. When the form is submitted, generate the step data and publish an update action to update user Dots tags
 */
export function StepFormEditDotsTags({
  step,
  insertVariables,
}: {
  step: TriggerStepCreate;
  insertVariables: PersonalizationValue[];
}) {
  invariant(step.type === "edit_dots_tags");
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const [newTag, setNewTag] = useState("");
  const [errorDuplicateTag, setErrorDuplicateTag] = useState(false);
  const readOnly = !state.trigger.current.isDraft;

  const {
    data: guildTags,
    isLoading: loading,
    addTag,
  } = useGuildTags(state.trigger.current.communityId);

  const defaultValues = {
    userId: step.configs.userIds[0] || "{{user.id}}",
    addTags: step.configs.addTags || [],
    removeTags: step.configs.removeTags || [],
    name: step.name,
  };

  const form = useForm({
    defaultValues,
  });

  const { handleSubmit, control, watch } = form;

  // update step
  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      //Just one user id is allowed for now
      stepDraft.configs.userIds = [data.userId];
      stepDraft.configs.addTags = data.addTags;
      stepDraft.configs.removeTags = data.removeTags;
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  // text input on change
  const onTagNameChange = (event: any) => {
    event.preventDefault();
    setNewTag(event.target.value);
    setErrorDuplicateTag(false);
  };

  // this functions creates a new tag if it doesn't exist
  const addItem = async () => {
    //Check if tag already exists
    if (
      guildTags
        .map((t) => t.name?.toLocaleLowerCase())
        .includes(newTag.toLocaleLowerCase())
    ) {
      setErrorDuplicateTag(true);
    } else {
      await addTag({
        name: newTag,
        description: "",
      });
      setNewTag("");
      window.analytics.track("New tag is added - edit tags step");
    }
  };

  const children: React.ReactNode[] = [];
  if (guildTags.length) {
    for (let i = 0; i < guildTags.length; i++) {
      children.push(
        <Select.Option
          key={`${guildTags[i].id}`}
          value={guildTags[i].id}
          name={guildTags[i].name}
        >
          {guildTags[i].name}
        </Select.Option>
      );
    }
  }

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <FormLabel label="Which user to apply">
        <DiscordMemberSingleSelector
          form={form}
          members={getMembersVariables(insertVariables)}
          defaultMemberId={defaultValues.userId}
          readOnly={readOnly}
          fieldName="userId"
        />
      </FormLabel>

      <FormLabel label="Dots tags to add">
        <Controller
          control={control}
          name="addTags"
          render={({ field: { onChange, onBlur, value } }) => {
            if (loading) {
              return <Spin />;
            } else {
              return (
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  allowClear
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  optionFilterProp="name"
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      <Divider style={{ margin: "4px 0" }} />
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "nowrap",
                          padding: 8,
                        }}
                      >
                        <Input
                          style={{ flex: "auto", borderRadius: 5 }}
                          value={newTag}
                          onChange={onTagNameChange}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                        <div className="pl-2">
                          <Button
                            type="primary"
                            style={{
                              flex: "none",
                              display: "block",
                              cursor: "pointer",
                            }}
                            onClick={addItem}
                            disabled={newTag === ""}
                          >
                            + New tag
                          </Button>
                        </div>
                      </div>
                      {errorDuplicateTag ? (
                        <p className="text-xs w-full text-center	text-red-400">
                          This tag already exists
                        </p>
                      ) : null}
                    </div>
                  )}
                >
                  {children}
                </Select>
              );
            }
          }}
        />
      </FormLabel>

      <FormLabel label="Dots tags to remove">
        <Controller
          control={control}
          name="removeTags"
          render={({ field: { onChange, onBlur, value } }) => {
            if (loading) {
              return <Spin />;
            } else {
              return (
                <Select
                  mode="multiple"
                  style={{ width: "100%", marginBottom: "1rem" }}
                  allowClear
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  optionFilterProp="name"
                >
                  {children}
                </Select>
              );
            }
          }}
        />
      </FormLabel>
    </StepFormWithCommonParts>
  );
}
